import React, { useEffect, useMemo, useState } from "react";
import { PrimaryButton } from "../../components";
import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import { useDropLocations } from "../../store/useDropLocations";
import { useMarketPricing } from "../../store/useMarketPricing";

interface Props {
  onApplyFilters: (filters: {
    status?: string;
    day?: number;
    month?: number;
    year?: number;
    locations?: string[];
    materials?: string[];
    search?: string;
  }) => void;
  onClearFilters: () => void;
  initialFilters?: {
    status?: string;
    day?: number;
    month?: number;
    year?: number;
    locations?: string[];
    materials?: string[];
    search?: string;
  };
}

type Period = "yearly" | "monthly" | "daily";

export function BeevisionAuditLogsFilters({
  onApplyFilters,
  onClearFilters,
  initialFilters,
}: Props) {
  const [status, setStatus] = useState<string | undefined>(
    initialFilters?.status
  );
  const [period, setPeriod] = useState<Period | undefined>(undefined);
  const [year, setYear] = useState<number | undefined>(initialFilters?.year);
  const [month, setMonth] = useState<number | undefined>(initialFilters?.month);
  const [day, setDay] = useState<number | undefined>(initialFilters?.day);
  const [dailyDateInput, setDailyDateInput] = useState<string>("");
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    initialFilters?.locations || []
  );
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>(
    initialFilters?.materials || []
  );
  const [search, setSearch] = useState<string>(initialFilters?.search || "");

  const { dropLocations, getDropLocations } = useDropLocations();
  const { marketPrices, getAllMarketPrices } = useMarketPricing();

  useEffect(() => {
    if (initialFilters) {
      setStatus(initialFilters.status);
      setYear(initialFilters.year);
      setMonth(initialFilters.month);
      setDay(initialFilters.day);
      setSelectedLocations(initialFilters.locations || []);
      setSelectedMaterials(initialFilters.materials || []);
      setSearch(initialFilters.search || "");

      if (initialFilters.day) {
        setPeriod("daily");
        const date = dayjs()
          .year(initialFilters.year || dayjs().year())
          .month((initialFilters.month || dayjs().month() + 1) - 1)
          .date(initialFilters.day);
        setDailyDateInput(date.format("YYYY-MM-DD"));
      } else if (initialFilters.month) {
        setPeriod("monthly");
      } else if (initialFilters.year) {
        setPeriod("yearly");
      }
    }
  }, [initialFilters]);

  useEffect(() => {
    getDropLocations();
    getAllMarketPrices();
  }, [getDropLocations, getAllMarketPrices]);

  const selectedLocationLabel = useMemo(() => {
    if (!selectedLocations.length) return "All Locations";
    if (selectedLocations.length === 1) {
      const loc = dropLocations?.find((l) => l._id === selectedLocations[0]);
      return loc?.name ?? "1 Location";
    }
    return `${selectedLocations.length} Locations`;
  }, [selectedLocations, dropLocations]);

  const selectedMaterialsLabel = useMemo(() => {
    if (!selectedMaterials.length) return "All Materials";
    if (selectedMaterials.length === 1) {
      const mp = marketPrices?.find((m) => m._id === selectedMaterials[0]);
      return mp?.name ?? "1 Material";
    }
    return `${selectedMaterials.length} Materials`;
  }, [selectedMaterials, marketPrices]);

  const toggleSelected = (current: string[], id?: string) => {
    if (!id) return current;
    return current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
  };

  const handleApplyFilters = () => {
    let filterDay = day;
    let filterMonth = month;
    let filterYear = year;

    if (period === "daily" && dailyDateInput) {
      const date = dayjs(dailyDateInput);
      if (date.isValid()) {
        filterYear = date.year();
        filterMonth = date.month() + 1; // dayjs months are 0-indexed
        filterDay = date.date();
      } else {
        // Handle invalid date input, maybe show an error or clear the field
        console.error("Invalid date format for daily filter");
        filterDay = undefined;
        filterMonth = undefined;
        filterYear = undefined;
      }
    } else if (period === "monthly") {
      filterDay = undefined; // Day is not applicable for monthly
    } else if (period === "yearly") {
      filterDay = undefined; // Day is not applicable for yearly
      filterMonth = undefined; // Month is not applicable for yearly
    }

    onApplyFilters({
      status,
      day: filterDay,
      month: filterMonth,
      year: filterYear,
      locations: selectedLocations,
      materials: selectedMaterials,
      search,
    });
  };

  const handleClearFilters = () => {
    setStatus(undefined);
    setPeriod(undefined);
    setYear(undefined);
    setMonth(undefined);
    setDay(undefined);
    setDailyDateInput("");
    setSelectedLocations([]);
    setSelectedMaterials([]);
    setSearch("");
    onClearFilters();
  };

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <div className="flex flex-row items-start justify-start">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
            Audit Log Filters
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-end justify-end mb-4 space-y-4 sm:space-y-0 sm:space-x-4">
        {/* Search */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search (user/material)"
          className="h-10 px-3 border rounded border-gray-border bg-white font-satoshi font-medium text-gray-text text-sm w-full sm:w-auto sm:flex-1"
        />

        {/* Locations Filter */}
        <Menu>
          <MenuHandler>
            <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
              {selectedLocationLabel}
              <ChevronDown className="ml-2" />
            </button>
          </MenuHandler>
          <MenuList
            className="rounded-none max-h-80 overflow-auto"
            placeholder={undefined}
          >
            <MenuItem
              onClick={() => setSelectedLocations([])}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              All Locations
            </MenuItem>
            {(dropLocations ?? []).map((loc) => (
              <MenuItem
                key={loc._id}
                onClick={() =>
                  setSelectedLocations((prev) => toggleSelected(prev, loc._id))
                }
                className="font-satoshi text-base text-dark flex items-center justify-between"
                placeholder={undefined}
              >
                <span>{loc.name}</span>
                {loc._id && selectedLocations.includes(loc._id) ? (
                  <span>✓</span>
                ) : null}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {/* Materials Filter */}
        <Menu>
          <MenuHandler>
            <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
              {selectedMaterialsLabel}
              <ChevronDown className="ml-2" />
            </button>
          </MenuHandler>
          <MenuList
            className="rounded-none max-h-80 overflow-auto"
            placeholder={undefined}
          >
            <MenuItem
              onClick={() => setSelectedMaterials([])}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              All Materials
            </MenuItem>
            {(marketPrices ?? []).map((mp) => (
              <MenuItem
                key={mp._id}
                onClick={() =>
                  setSelectedMaterials((prev) => toggleSelected(prev, mp._id))
                }
                className="font-satoshi text-base text-dark flex items-center justify-between"
                placeholder={undefined}
              >
                <span>{mp.name}</span>
                {mp._id && selectedMaterials.includes(mp._id) ? (
                  <span>✓</span>
                ) : null}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>

        {/* Status Filter */}
        <Menu>
          <MenuHandler>
            <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
              {status
                ? status.charAt(0).toUpperCase() + status.slice(1)
                : "All Statuses"}
              <ChevronDown className="ml-2" />
            </button>
          </MenuHandler>
          <MenuList placeholder={undefined}>
            <MenuItem
              onClick={() => setStatus(undefined)}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              All Statuses
            </MenuItem>
            <MenuItem
              onClick={() => setStatus("approved")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Approved
            </MenuItem>
            <MenuItem
              onClick={() => setStatus("rejected")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Rejected
            </MenuItem>
            <MenuItem
              onClick={() => setStatus("pending")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Pending
            </MenuItem>
            <MenuItem
              onClick={() => setStatus("webhook_rejected")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Webhook Rejected
            </MenuItem>
          </MenuList>
        </Menu>

        {/* Period Filter */}
        <Menu>
          <MenuHandler>
            <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
              {period
                ? period.charAt(0).toUpperCase() + period.slice(1)
                : "Select Period"}
              <ChevronDown className="ml-2" />
            </button>
          </MenuHandler>
          <MenuList placeholder={undefined}>
            <MenuItem
              onClick={() => setPeriod("yearly")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Yearly
            </MenuItem>
            <MenuItem
              onClick={() => setPeriod("monthly")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Monthly
            </MenuItem>
            <MenuItem
              onClick={() => setPeriod("daily")}
              className="font-satoshi text-base text-dark"
              placeholder={undefined}
            >
              Daily
            </MenuItem>
          </MenuList>
        </Menu>

        {period === "yearly" || period === "monthly" ? (
          <Menu>
            <MenuHandler>
              <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {year || "Select Year"}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                onClick={() => setYear(undefined)}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Select Year
              </MenuItem>
              {years.map((y) => (
                <MenuItem
                  key={y}
                  onClick={() => setYear(y)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {y}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : null}

        {period === "monthly" ? (
          <Menu>
            <MenuHandler>
              <button className="h-10 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {month
                  ? dayjs()
                      .month(month - 1)
                      .format("MMMM")
                  : "Select Month"}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                onClick={() => setMonth(undefined)}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Select Month
              </MenuItem>
              {months.map((m) => (
                <MenuItem
                  key={m}
                  onClick={() => setMonth(m)}
                  className="font-satoshi text-base text-dark"
                  placeholder={undefined}
                >
                  {dayjs()
                    .month(m - 1)
                    .format("MMMM")}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : null}

        {period === "daily" ? (
          <input
            type="date"
            value={dailyDateInput}
            onChange={(e) => setDailyDateInput(e.target.value)}
            className="h-10 px-3 border rounded border-gray-border bg-white font-satoshi font-medium text-gray-text text-sm"
          />
        ) : null}
      </div>
      <div className="flex flex-row items-end justify-end space-x-4">
        {/* Action Buttons */}
        <PrimaryButton
          label="Apply Filters"
          onClick={handleApplyFilters}
          className="h-10"
        />
        <PrimaryButton
          label="Clear Filters"
          onClick={handleClearFilters}
          className="h-10 bg-gray-200 text-gray-700"
        />
      </div>
    </div>
  );
}
